import { Box, BoxProps, ColorOptions, ModBox, Text, TextNewProps, TextProps } from '@jfs/ui-new'
import { ButtonNew } from '../AdminButton'
import { useCallback, useEffect, useState } from 'react'
import { FlatList } from 'react-native'
import { shadow } from 'styled-system'
import { Pagination } from '../../screens/FraudAndRiskManagement/Pagination'
import { Container } from '../Container'
import { ListHead } from '../ListHead'
import { ListRow } from '../ListRow'
import { SearchFilter } from './SearchFilter'

interface ListNewProps {
  listTitle?: string
  searchData?: SearchDataProps[][]
  fetchDataFromServer: any
  isSearchButtonLoading?: boolean
  apiHandler: { data: any; state: 'pending' | 'fulfilled' | 'rejected' | undefined }
  data: any[]
  onRowItemClick: any
  updatedPaginationInfo: any
  keyExtractor: any
  headTitle: any
  sizeConfig: any
  showExpandArrow?: boolean
  type?: string
  onAllChecked?: (isAllChecked) => void
  isAllChecked?: boolean
  columnsWidth?: any
  onAction?: (item: any, index: number) => void
  expandView?: any
  isExpanded?: boolean
  expandedIndex?: number
  onEdit?: (item?: any) => void
  onDelete?: (item?: any) => void
  hrefPath?: string
  isActionLoading?: boolean
  loadingIndex?: number
  onChecked?: (isChecked, index) => void
  showSyncPam?: boolean
  onSync?: () => void
  exportFile?: (data: any) => void
  exportFile2?: (data: any) => void
  searchFormRef?: any
  dbCollumnMapper?: any
  hideTitle?: any
  isExportButtonLoading?: boolean
  cellTextStyle?: TextProps
  headerTextStyle?: TextNewProps
}

export interface SearchDataProps {
  key: string
  type: 'string' | 'dropdown' | 'date'
  placeHolder: string
  data?: { id: number; value: string }[]
}

const DEFAULT_PAGINATION = {
  size: 10,
  page: 1,
  total: 0,
}

const SORT_NOT_REQ_CALLUMN = ['duration', 'link', 'group']

export const ListNew = ({
  listTitle,
  searchData = [],
  fetchDataFromServer,
  isSearchButtonLoading = false,
  apiHandler,
  data = [],
  onRowItemClick,
  keyExtractor,
  headTitle,
  sizeConfig,
  updatedPaginationInfo,
  showExpandArrow,
  type = 'DEFAULT',
  onAllChecked,
  isAllChecked = false,
  columnsWidth,
  onAction,
  expandView,
  isExpanded,
  expandedIndex,
  onEdit,
  onDelete,
  hrefPath,
  isActionLoading,
  loadingIndex,
  onChecked,
  showSyncPam,
  onSync,
  exportFile,
  exportFile2,
  searchFormRef,
  dbCollumnMapper,
  hideTitle,
  isExportButtonLoading,
  cellTextStyle,
  headerTextStyle,
}: ListNewProps) => {
  const [paginationInfo, setPaginationInfo] = useState(DEFAULT_PAGINATION)
  const [sortArr, setSortArr] = useState<any[]>(
    headTitle
      ?.filter((item) => !SORT_NOT_REQ_CALLUMN.includes(Object.keys(item)[0]))
      ?.map((item: any, index: number) => {
        return { order: 'asc', index: index, key: Object.keys(item)[0] }
      }),
  )

  const [sortKeyInfo, setSortKeyInfo] = useState({
    sortByColumn: undefined,
    sortDirection: undefined,
  })

  useEffect(() => {
    setPaginationInfo({ ...paginationInfo, ...updatedPaginationInfo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedPaginationInfo])

  const onPageClick = useCallback(
    (pageNumber: number) => {
      setPaginationInfo({ ...paginationInfo, page: pageNumber })
      fetchDataFromServer(searchFormRef?.current?.getValues(), { ...paginationInfo, ...sortKeyInfo, page: pageNumber })
    },
    [fetchDataFromServer, paginationInfo, searchFormRef, sortKeyInfo],
  )

  const onItemCountSelect = useCallback(
    (count: string) => {
      setPaginationInfo({ ...paginationInfo, page: 1, size: parseInt(count, 10) })
      fetchDataFromServer(searchFormRef?.current?.getValues(), { ...sortKeyInfo, page: 1, size: parseInt(count, 10) })
    },
    [fetchDataFromServer, paginationInfo, searchFormRef, sortKeyInfo],
  )

  const onSort = (key: any) => {
    if (!SORT_NOT_REQ_CALLUMN.includes(key)) {
      let sortingOrder = 'ASC'
      const sortedArr = sortArr.map((item) => {
        if (item.key.toLowerCase().replace(' ', '') === key.toLowerCase().replace(' ', '')) {
          sortingOrder = item.order === 'asc' ? 'DESC' : 'ASC'
          return {
            ...item,
            order: item.order === 'asc' ? 'desc' : 'asc',
          }
        } else {
          return { ...item, order: 'asc' }
        }
      })
      setSortArr(sortedArr)
      setSortKeyInfo({
        sortByColumn: key?.toLowerCase() === 'id' ? 'id' : dbCollumnMapper?.[key] ?? key,
        sortDirection: sortingOrder,
      })
      fetchDataFromServer(searchFormRef?.current?.getValues(), {
        ...paginationInfo,
        page: 1,
        sortByColumn: key?.toLowerCase() === 'id' ? 'id' : dbCollumnMapper?.[key] ?? key,
        sortDirection: sortingOrder,
      })
    }
  }
  const _renderItem = useCallback(
    (data: any) => {
      return (
        <ListRow
          onPress={() => onRowItemClick(data)}
          item={data.item}
          headTitle={headTitle}
          index={data.index}
          sizeConfig={sizeConfig}
          onAction={() => onAction && onAction(data.item, data.index)}
          expandView={expandView}
          isExpanded={isExpanded}
          expandedIndex={expandedIndex}
          showExpandArrow={showExpandArrow}
          onEdit={() => onEdit && onEdit(data)}
          onDelete={() => onDelete && onDelete(data)}
          hrefPath={hrefPath}
          isActionLoading={isActionLoading}
          loadingIndex={loadingIndex}
          type={type}
          onChecked={onChecked}
          columnsWidth={columnsWidth}
          cellTextStyle={cellTextStyle}
        />
      )
    },
    [
      cellTextStyle,
      columnsWidth,
      expandView,
      expandedIndex,
      headTitle,
      hrefPath,
      isActionLoading,
      isExpanded,
      loadingIndex,
      onAction,
      onChecked,
      onDelete,
      onEdit,
      onRowItemClick,
      showExpandArrow,
      sizeConfig,
      type,
    ],
  )

  const onSearchClick = useCallback(() => {
    setPaginationInfo({ ...paginationInfo, page: 1 })
    fetchDataFromServer(searchFormRef?.current?.getValues(), { ...sortKeyInfo, page: 1, size: paginationInfo.size })
  }, [fetchDataFromServer, paginationInfo, searchFormRef, sortKeyInfo])

  return (
    <>
      <ModBox {...styles.container} show={listTitle && listTitle?.length > 0 && !hideTitle ? true : false}>
        <Text fontFamily="jiotype-bold" color={'#214796' as ColorOptions} fontSize={16} title={listTitle} />
      </ModBox>
      <Box {...styles.listContainer}>
        <SearchFilter
          searchData={searchData}
          onSearchClick={onSearchClick}
          isLoading={isSearchButtonLoading}
          formRef={searchFormRef}
        />
        <Container
          isLoading={apiHandler.state === 'pending'}
          errorMessage={
            apiHandler?.data?.message ||
            ((apiHandler?.state === 'fulfilled' && data.length === 0 && 'No Data Found') as any)
          }
          borderTopWidth={1}
          borderColor={'BORDER_COLOR'}
          onRetry={() =>
            fetchDataFromServer(searchFormRef?.current?.getValues(), { ...paginationInfo, ...sortKeyInfo })
          }
          nativeID="vertical-scroll"
          height={undefined}
          flexGrow={1}
          minHeight={Math.max(data?.length * 32 + 50, 300)}>
          <Box flexDirection={'row'} align={'center'} gap={16} pv={8} ph={16} justifyContent={'flex-end'}>
            <ButtonNew
              title={'Sync PAM'}
              onPress={() => onSync && onSync()}
              height={30}
              color={'GREEN'}
              hide={!showSyncPam}
              borderRadius={4}
            />
            <ButtonNew
              title={'Export To Excel'}
              onPress={() => exportFile && exportFile(data)} //htag need to check
              height={32}
              borderRadius={4}
              width={148}
              isLoading={isExportButtonLoading}
              hide={
                listTitle !== 'Merchant Mapping Approvals' &&
                listTitle !== 'Merchant Mapping List' &&
                listTitle !== 'Dynamic Hold Settlements' &&
                listTitle !== 'Risk Controls - Transactions - Approvals' &&
                listTitle !== 'Risk Controls - MID - Approvals' &&
                listTitle !== 'Payout CMS - Approvals' &&
                listTitle !== 'Credit Debit Adjustment Approvals' &&
                listTitle !== 'Merchant payout Config Approvals' &&
                listTitle !== 'Users' &&
                listTitle !== 'Merchant Id to Package Approvals' &&
                listTitle !== 'Mapping List' &&
                listTitle !== 'Package Name List'
              }
              hoverColor={'DARK_OCEAN_BLUE'}
            />
            <ButtonNew
              title={'Export Data For Key Exchange'}
              onPress={() => exportFile2 && exportFile2(data)} //htag need to check
              height={32}
              borderRadius={4}
              isLoading={isExportButtonLoading}
              hide={listTitle !== 'Merchant Mapping List'}
              hoverColor={'DARK_OCEAN_BLUE'}
            />
          </Box>
          <ScrollContainer type={type}>
            <ListHead
              headTitle={headTitle}
              sizeConfig={sizeConfig}
              showExpandArrow={showExpandArrow}
              isColumnSearchEnabled={false}
              onSearchTextChange={undefined}
              type={type}
              onAllChecked={onAllChecked}
              isAllChecked={isAllChecked}
              columnsWidth={columnsWidth}
              onSort={onSort}
              sortData={sortArr}
              headerTextStyle={headerTextStyle}
            />
            <FlatList
              keyExtractor={keyExtractor}
              data={data}
              renderItem={_renderItem}
              style={styles.flatListContainer(type)}
            />
          </ScrollContainer>
        </Container>
        <Pagination
          dataWindow={paginationInfo.size}
          onPageClick={onPageClick}
          onItemsCountSelect={onItemCountSelect}
          pageNumber={paginationInfo.page}
          totalPages={Math.ceil(paginationInfo.total / paginationInfo.size)}
          total={paginationInfo.total}
        />
      </Box>
    </>
  )
}

const styles = {
  container: {
    ...shadow,
    underlayColor: 'WHITE',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderWidth: 1,
    borderRadius: 4,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderColor: '#e3e6f0' as ColorOptions,
    backgroundColor: '#f8f9fc' as ColorOptions,
  } as BoxProps,
  listContainer: {
    ...shadow,
    backgroundColor: 'WHITE',
    padding: 0,
    underlayColor: 'WHITE',
    borderColor: '#e3e6f0' as ColorOptions,
    borderWidth: 1,
    borderTopWidth: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  } as BoxProps,
  flatListContainer: (type) => (type === 'SCROLLABLE' ? { width: '100%', minWidth: 'min-content' } : ({} as any)),
}

const ScrollContainer = ({ type, children }) => {
  return (
    <>
      {type === 'SCROLLABLE' && (
        <Box overflowX={'auto'} width={'100%'} flex={1}>
          {children}
        </Box>
      )}
      {type !== 'SCROLLABLE' && children}
    </>
  )
}
